import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AutoComplete, Form, Input, Select } from 'antd';
import { CloseOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import useAxiosClient from '../../axios-client.js';
import { useDivisions } from '../../hooks/useDivisions.jsx';
import { useDistricts } from '../../hooks/useDistricts.jsx';
import { useUpazilas } from '../../hooks/useUpazilas.jsx';
import { Mono } from '../Order/orderFormShared.jsx';
import { SHELL_THEME as THEME, STATUS } from '../../shellTokens';
// The chrome this component draws itself in — .et-of-chip, .et-of-cols3 — is
// the order form's, so it carries the stylesheet rather than relying on
// whichever screen happens to host it having imported it.
import '../Order/orderForm.css';

const { Option } = Select;

/** Below this many digits the directory search is noise, so it doesn't run. */
const MIN_DIGITS = 3;

/**
 * Find a customer by phone, or register one.
 *
 * The buyer on a fabric sale is the same Customer the order side uses, so this
 * searches the one directory rather than letting a name be typed in twice and
 * drift. Creating one goes through /customers/store, which requires a full
 * address (street + division + district) — that is the directory's rule, not
 * this form's, and the fields here mirror the order form's Customer card.
 *
 * The lookup shows its state — idle, searching, matched, none — for the reason
 * the order form's does: "did this find someone?" is the only question asked of
 * this control, and a silent autofill never answers it.
 *
 * Shaped as a controlled field (`value` / `onChange`), so it drops straight
 * into a Form.Item and validates like any other control.
 */
function CustomerPicker({ value, onChange, type = 'offline', darkMode = false }) {
  const axiosClient = useAxiosClient();

  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounce = useRef(null);

  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newForm] = Form.useForm();

  // The geocode selects cascade, so the two upper ids are held outside the form
  // to drive the hooks that fetch the next list down.
  const [divisionId, setDivisionId] = useState(null);
  const [districtId, setDistrictId] = useState(null);

  const { divisions, divisionLoading } = useDivisions();
  const { districts, districtLoading } = useDistricts(divisionId);
  const { upazilas, upazilaLoading } = useUpazilas(districtId);

  // The AutoComplete's dropdown portals to <body>, outside the element the
  // shell declares its CSS variables on, so an option's colours have to be hex.
  const t = THEME[darkMode ? 'dark' : 'light'];

  const emit = useCallback((customer) => {
    if (typeof onChange === 'function') onChange(customer);
  }, [onChange]);

  useEffect(() => () => { if (debounce.current) clearTimeout(debounce.current); }, []);

  const handleSearch = useCallback((text) => {
    setQuery(text);
    if (debounce.current) clearTimeout(debounce.current);

    const digits = (text || '').replace(/\D/g, '');
    if (digits.length < MIN_DIGITS) {
      setOptions([]);
      setSearched(false);
      return;
    }

    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axiosClient.get('/customers/searchByPhone', {
          params: { phone: text.trim(), type },
        });
        setOptions((res.data || []).map((c) => ({
          value: String(c.id),
          customer: c,
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0' }}>
              <span style={{ ...avatar, background: t.activeBg, color: t.activeText }}>
                {(c.name || '?').trim().charAt(0).toUpperCase()}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25 }}>
                <span style={{ font: '600 13px Outfit, sans-serif', color: t.text }}>{c.name}</span>
                <Mono style={{ fontSize: 11.5, color: t.muted }}>{c.phone}</Mono>
              </span>
              {c.ordersCount != null && (
                <span style={{ marginLeft: 'auto', font: '400 11px Outfit, sans-serif', color: t.dim }}>
                  {c.ordersCount} order{c.ordersCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
          ),
        })));
        setSearched(true);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not search customers');
      } finally {
        setSearching(false);
      }
    }, 400);
  }, [axiosClient, type, t]);

  const handleSelect = (_v, option) => {
    if (!option?.customer) return;
    emit(option.customer);
    setQuery('');
    setOptions([]);
    setSearched(false);
  };

  /** Pre-fills the phone already typed — it is nearly always the one being registered. */
  const openAdd = () => {
    newForm.setFieldsValue({ phone: query.trim() });
    setAdding(true);
  };

  const closeAdd = () => {
    setAdding(false);
    newForm.resetFields();
    setDivisionId(null);
    setDistrictId(null);
  };

  const submitNew = async () => {
    let values;
    try {
      values = await newForm.validateFields();
    } catch {
      return; // antd has already marked the offending fields
    }

    setSaving(true);
    try {
      const res = await axiosClient.post('/customers/store', {
        name: values.name.trim(),
        phone: values.phone.trim(),
        address: values.address.trim(),
        altPhone: values.altPhone?.trim() || null,
        facebookId: values.facebookId?.trim() || null,
        division: values.division,
        district: values.district,
        upazila: values.upazila || null,
      });

      emit({
        id: res.data.id,
        name: values.name.trim(),
        phone: values.phone.trim(),
        address: values.address.trim(),
        ordersCount: 0,
      });
      toast.success('Customer added.');
      closeAdd();
      setQuery('');
      setSearched(false);
    } catch (error) {
      // 403 here means the operator can manage fabric but not the customer
      // directory — worth saying plainly rather than as a bare "Forbidden".
      const message = error.response?.status === 403
        ? 'You do not have permission to add customers. Ask an admin for customer access.'
        : error.response?.data?.message || 'Could not add the customer';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // A customer is chosen. Same green "matched" strip the order form promotes a
  // phone match to, so the state reads identically on both screens.
  if (value) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '10px 12px', borderRadius: 9,
          background: 'rgba(52,211,153,.10)', border: '1px solid rgba(52,211,153,.28)',
        }}
      >
        <span style={{ ...avatar, background: 'var(--activeBg)', color: 'var(--activeText)' }}>
          {(value.name || '?').trim().charAt(0).toUpperCase()}
        </span>
        <span style={{ font: '600 13px Outfit, sans-serif', color: 'var(--text)' }}>{value.name}</span>
        <Mono style={{ fontSize: 12, color: 'var(--muted)' }}>{value.phone}</Mono>
        <span style={{ font: '400 12px Outfit, sans-serif', color: STATUS.ok }}>
          {value.ordersCount != null
            ? `${value.ordersCount} previous order${value.ordersCount === 1 ? '' : 's'}`
            : 'Existing customer'}
        </span>
        <button type="button" className="et-of-chip" style={{ marginLeft: 'auto' }} onClick={() => emit(null)}>
          Not this person
        </button>
      </div>
    );
  }

  // Not "no match" until the search in flight has come back — `searched` is
  // still true from the previous term, which is what kept "+ Add customer"
  // showing next to "Searching…".
  const noMatch = searched && !searching && options.length === 0;
  const hint = searching
    ? 'Searching…'
    : noMatch
      ? 'No customer on that number.'
      : 'Type a phone number to find the buyer in the customer directory.';

  return (
    <div>
      <div className="et-of-lookup">
        <div style={{ flex: 1, minWidth: 0, maxWidth: 320 }}>
          <AutoComplete
            options={options}
            onSearch={handleSearch}
            onSelect={handleSelect}
            value={query}
            style={{ width: '100%' }}
            // No panel while searching. antd's default Spin is a four-dot
            // cluster in the accent colour, and one in the prefix plus another
            // in the dropdown made a phone lookup look like it was doing two
            // things at once. The prefix carries the state; the hint carries
            // the words.
            notFoundContent={null}
          >
            <Input
              placeholder="01XXXXXXXXX"
              className="et-of-mono"
              prefix={searching
                ? <LoadingOutlined spin style={{ color: 'var(--muted)', fontSize: 12 }} />
                : <SearchOutlined style={{ color: 'var(--dim)' }} />}
            />
          </AutoComplete>
        </div>
        <div
          style={{
            flex: 1, paddingTop: 9,
            font: '400 12.5px/1.45 Outfit, sans-serif',
            color: noMatch && !searching ? STATUS.warn : 'var(--muted)',
            display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
          }}
        >
          <span>{hint}</span>
          {/* Only offered once a search has actually come back empty, so it does
              not invite creating a duplicate of someone already listed. */}
          {noMatch && !adding && (
            <button type="button" className="et-of-chip et-of-chip--primary" onClick={openAdd}>
              + Add customer
            </button>
          )}
        </div>
      </div>

      {adding && (
        <div style={{ marginTop: 12, borderRadius: 11, border: '1px solid var(--border)', background: 'var(--surface2)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ font: '700 13px Outfit, sans-serif' }}>New customer</div>
              <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 2 }}>
                Added to the shared directory, the same one orders draw from.
              </div>
            </div>
            <span
              role="button"
              tabIndex={0}
              onClick={closeAdd}
              onKeyDown={(e) => { if (e.key === 'Enter') closeAdd(); }}
              style={{
                flex: 'none', width: 26, height: 26, borderRadius: 7, background: 'var(--surface)',
                color: 'var(--muted)', fontSize: 11, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer',
              }}
            >
              <CloseOutlined />
            </span>
          </div>

          <div style={{ padding: 14 }}>
            {/* A div rather than a <form>: nesting one inside the sale form
                that hosts this is invalid HTML. Not `component={false}` —
                that renders no element, and antd's vertical layout is only the
                CSS behind the `ant-form-vertical` class on that element. */}
            <Form form={newForm} layout="vertical" component="div">
              <div className="et-of-cols3" style={{ marginBottom: 12 }}>
                <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name!' }]}>
                  <Input placeholder="Customer name" />
                </Form.Item>
                <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Please enter a phone number!' }]}>
                  <Input placeholder="01XXXXXXXXX" className="et-of-mono" />
                </Form.Item>
                <Form.Item name="altPhone" label="Alt. phone">
                  <Input placeholder="Optional" className="et-of-mono" />
                </Form.Item>
              </div>

              <div className="et-of-cols3" style={{ marginBottom: 12 }}>
                <Form.Item name="division" label="Division" rules={[{ required: true, message: 'Please select division!' }]}>
                  <Select
                    placeholder="Select division"
                    loading={divisionLoading}
                    onSelect={(v) => {
                      setDivisionId(v);
                      setDistrictId(null);
                      newForm.setFieldsValue({ district: undefined, upazila: undefined });
                    }}
                  >
                    {divisions.map((d) => <Option value={d.id} key={d.id}>{d.name}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="district" label="District" rules={[{ required: true, message: 'Please select district!' }]}>
                  <Select
                    placeholder={districts.length ? 'Select district' : 'Pick a division first'}
                    loading={districtLoading}
                    disabled={!districts.length}
                    onSelect={(v) => {
                      setDistrictId(v);
                      newForm.setFieldsValue({ upazila: undefined });
                    }}
                  >
                    {districts.map((d) => <Option value={d.id} key={d.id}>{d.name}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="upazila" label="Upazila / Thana">
                  <Select
                    placeholder={upazilas.length ? 'Select upazila' : 'Pick a district first'}
                    loading={upazilaLoading}
                    disabled={!upazilas.length}
                    allowClear
                  >
                    {upazilas.map((u) => <Option value={u.id} key={u.id}>{u.name}</Option>)}
                  </Select>
                </Form.Item>
              </div>

              <div className="et-of-cols3">
                <Form.Item name="address" label="Street address" className="et-of-cols3__wide" rules={[{ required: true, message: 'Please enter an address!' }]}>
                  <Input placeholder="House, road, area" />
                </Form.Item>
                <Form.Item name="facebookId" label="Reference" className="et-of-cols3__wide">
                  <Input placeholder="Optional — how you know them" />
                </Form.Item>
              </div>
            </Form>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button type="button" className="et-of-chip" onClick={closeAdd}>Cancel</button>
              <button
                type="button"
                className="et-of-chip et-of-chip--primary"
                onClick={submitNew}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Add customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const avatar = {
  flex: 'none', width: 26, height: 26, borderRadius: 8,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  font: '700 11px Outfit, sans-serif',
};

export default CustomerPicker;
